import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import { GroupedInventoryItem } from "../store/inventoryStore";
import { Link } from "react-router-dom";

// Типы
interface InventoryTableProps {
  data: GroupedInventoryItem[];
  isLoading?: boolean;
  onEdit: (size: any, product: GroupedInventoryItem) => void;
  onDelete: (id: number) => void;
  onSold: (id: number) => void;
  onHide: (id: number, isCurrentlyInStore: boolean) => void;
  onShipment: (sneakerId: number, productName: string) => void;
  onCopyLink: (id: number) => void;
  onGeneratePromoCode: (id: number) => void;
  formatPrice: (price: string) => string;
  formatMarketPrice: (price: string) => string;
  loadingProductData: boolean;
  accessStockroom: boolean;
  // Пагинация
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

// Стилизованные компоненты
const TableWrapper = styled.div`
  width: 100%;
  overflow: visible;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  flex: 1;
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
`;

const FilterRow = styled.div`
  display: none;
  user-select: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 2fr 1.2fr 1fr 1fr 1fr;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin-bottom: 8px;
    column-gap: 6px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr 1.1fr;
    column-gap: 10px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr 1.2fr;
    column-gap: 12px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr 1.2fr;
    column-gap: 14px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr 1.3fr;
    column-gap: 16px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 0 20px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 0 24px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 0 32px;
  }
`;

const FilterItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  width: 100%;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.$clickable ? "rgba(96, 159, 148, 0.08)" : "transparent"};
  }

  &:first-child {
    justify-content: flex-start;
  }
`;

const FilterLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609f94;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const SortIcon = styled.div<{ direction: "asc" | "desc" | "none" }>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  flex-shrink: 0;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.direction === "none" ? 0.3 : 1)};

  ${(props) => {
    if (props.direction === "asc") {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    } else if (props.direction === "desc") {
      return `
        border-top: 6px solid #609F94;
        border-bottom: none;
      `;
    } else {
      return `
        border-bottom: 6px solid #609F94;
        border-top: none;
      `;
    }
  }}

  @media (min-width: 768px) {
    border-left-width: 5px;
    border-right-width: 5px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "7px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "7px" : "0")};
  }

  @media (min-width: 1024px) {
    border-left-width: 6px;
    border-right-width: 6px;
    border-bottom-width: ${(props) =>
      props.direction === "asc" || props.direction === "none" ? "8px" : "0"};
    border-top-width: ${(props) => (props.direction === "desc" ? "8px" : "0")};
  }
`;

const CardWithExpandWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
`;

const CardContainer = styled.div<{ $expanded?: boolean }>`
  background-color: #1a2e2e;
  border-radius: 12px;
  border: 1px solid #2c4a4a;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  position: relative;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
    border-color: #3d5a5a;
  }
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-height: 80px;
  column-gap: 8px;
  padding: 12px;
  overflow: visible;
  box-sizing: border-box;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1.2fr 1fr 1fr 1fr;
    column-gap: 6px;
    padding: 12px;
    min-height: 60px;
    max-width: 100%;
  }

  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr 1.1fr;
    column-gap: 10px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr 1.2fr;
    column-gap: 12px;
  }

  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr 1.2fr;
    column-gap: 14px;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr 1.3fr;
    column-gap: 16px;
  }

  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 12px 20px;
  }

  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 12px 24px;
  }

  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 12px 32px;
  }
`;

const MobileProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileProductHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MobileProductInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid #2c4a4a;
  gap: 8px;
`;

const MobileArticleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileProductPrice = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #24ba7d;
`;

const MobileProductArticle = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #8b9ba8;
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  @media (min-width: 768px) and (max-width: 1279px) {
    gap: 6px;
  }
`;

const ProductImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: transparent;
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 6px;
  }

  @media (min-width: 900px) {
    width: 52px;
    height: 52px;
    border-radius: 6px;
  }

  @media (min-width: 1024px) {
    width: 56px;
    height: 56px;
    border-radius: 7px;
  }

  @media (min-width: 1280px) {
    width: 64px;
    height: 64px;
    border-radius: 7px;
  }

  @media (min-width: 1440px) {
    width: 80px;
    height: 80px;
    border-radius: 8px;
  }

  @media (min-width: 1680px) {
    width: 96px;
    height: 96px;
    border-radius: 10px;
  }

  @media (min-width: 1920px) {
    width: 112px;
    height: 112px;
    border-radius: 12px;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const ProductName = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1920px) {
    font-size: 15px;
  }
`;

const ProductModel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #8b9ba8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 12px;
  }

  @media (min-width: 1920px) {
    font-size: 13px;
  }
`;

const ProductColor = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 10px;
  color: #609f94;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 1024px) {
    font-size: 11px;
  }

  @media (min-width: 1920px) {
    font-size: 12px;
  }
`;

const DesktopContent = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SizeLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;

  @media (min-width: 768px) {
    font-size: 11px;
    gap: 3px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
    gap: 4px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
    gap: 5px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
    gap: 6px;
  }
`;

const SizesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
`;

const SizeChip = styled.span`
  background-color: #0e1f1f;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 10px;
    padding: 3px 6px;
  }

  @media (min-width: 900px) {
    font-size: 10px;
    padding: 4px 7px;
  }

  @media (min-width: 1280px) {
    font-size: 11px;
    padding: 5px 8px;
  }

  @media (min-width: 1440px) {
    font-size: 12px;
    padding: 6px 10px;
  }

  @media (min-width: 1920px) {
    font-size: 14px;
    padding: 8px 12px;
  }
`;

const SizeCount = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #609f94;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const PriceLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 12px;
  color: #24ba7d;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1280px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }
`;

const ArticleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ArticleLabel = styled.div`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #8b9ba8;
  display: flex;
  align-items: center;
  justify-content: center;
  word-break: break-all;

  @media (min-width: 768px) {
    font-size: 10px;
  }

  @media (min-width: 900px) {
    font-size: 11px;
  }

  @media (min-width: 1280px) {
    font-size: 12px;
  }

  @media (min-width: 1440px) {
    font-size: 13px;
  }
`;

const CopyArticleButton = styled.button`
  background: #1a2e2e;
  border: 1px solid #2c4a4a;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding: 0;
  color: #8b9ba8;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(96, 159, 148, 0.15);
    border-color: #609f94;
    color: #609f94;
  }

  &:active {
    background-color: rgba(96, 159, 148, 0.25);
    transform: scale(0.95);
  }

  svg {
    width: 13px;
    height: 13px;
    opacity: 0.8;
  }

  @media (min-width: 768px) {
    width: 26px;
    height: 26px;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  @media (min-width: 900px) {
    width: 28px;
    height: 28px;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  @media (min-width: 1280px) {
    width: 30px;
    height: 30px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (min-width: 1440px) {
    width: 32px;
    height: 32px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const ExpandableContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${props => props.$expanded ? 'auto' : '0'};
  opacity: ${props => props.$expanded ? '1' : '0'};
  transition: max-height 0.3s ease, opacity 0.3s ease;
  background-color: #132525;

  @media (max-width: 767px) {
    max-height: ${props => props.$expanded ? '8000px' : '0'};
  }
`;

const SizeVariantContainer = styled.div`
  padding: 16px;
  border-top: 1px solid #2c4a4a;

  @media (min-width: 768px) {
    padding: 16px 20px;
  }

  @media (min-width: 1024px) {
    padding: 20px;
  }
`;

const SizeVariantContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SizeVariantInfo = styled.div`
  flex: 1;
`;

const SizeVariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const SizeVariantActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    margin-top: 12px;
    border-top: 1px solid #2c4a4a;
    padding-top: 12px;
  }

  @media (min-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const InfoPill = styled.div`
  background-color: #0E1F1F;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    padding: 10px 14px;
  }
`;

const InfoLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-size: 10px;
  color: #609F94;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 1024px) {
    font-size: 12px;
  }
`;

const InfoValue = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  color: #FFFFFF;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }
`;

const ProductLink = styled(Link)`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #609f94;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: color 0.2s ease;

  &:hover {
    color: #24ba7d;
  }

  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

const SizeVariantPrice = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #24BA7D;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 20px;
  }

  @media (min-width: 1440px) {
    font-size: 20px;
  }

  @media (min-width: 1920px) {
    font-size: 20px;
  }
`;

const StatusButton = styled.button<{ $color: string }>`
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.$color};
  background-color: transparent;
  color: ${(props) => props.$color};
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => props.$color};
    color: #0e1f1f;
    transform: translateY(-1px);
  }

`;

const IconButtonsColumn = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  justify-content: center;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 6px;
  }
`;

const IconButton = styled.button`
  background: #1a2e2e;
  border: 1px solid #2c4a4a;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(96, 159, 148, 0.15);
    border-color: #609f94;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:active {
    background-color: rgba(96, 159, 148, 0.2);
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.85;
  }

  @media (min-width: 768px) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0 12px;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    padding: 0 16px;
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.16)' : 'rgba(8, 28, 33, 0.48)'};
  border: 1px solid ${props => props.$active ? '#24BA7D' : 'rgba(255, 255, 255, 0.16)'};
  color: ${props => props.$active ? '#24BA7D' : '#FFFFFF'};
  font-family: 'Ubuntu', sans-serif;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.24)' : 'rgba(8, 28, 33, 0.64)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  margin-right: 0;
  text-align: center;
  flex: 1;
  min-width: 200px;
  
  @media (min-width: 768px) {
    font-size: 14px;
    margin-right: auto;
    flex: none;
    min-width: auto;
  }
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0;
  
  @media (min-width: 768px) {
    margin-left: 16px;
  }
`;

const ItemsPerPageLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin-right: 8px;
`;

const ItemsPerPageSelect = styled.select`
  background-color: rgba(8, 28, 33, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(36, 186, 125, 0.48);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background-color: #172A2A;
  }
`;

// Вспомогательная функция для группировки размеров
const groupSizesByValue = (sizes: any[]) => {
  const grouped: { [key: string]: number } = {};
  sizes.forEach((size) => {
    const sizeValue = size.size;
    grouped[sizeValue] = (grouped[sizeValue] || 0) + 1;
  });
  return Object.entries(grouped).map(([size, count]) => ({ size, count }));
};

const InventoryTable: React.FC<InventoryTableProps> = ({
  data,
  isLoading,
  onEdit,
  onDelete,
  onSold,
  onHide,
  onShipment,
  onCopyLink,
  onGeneratePromoCode,
  formatPrice,
  formatMarketPrice,
  loadingProductData,
  accessStockroom,
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

  // Определяем колонки для таблицы
  const columns = useMemo<ColumnDef<GroupedInventoryItem>[]>(
    () => [
      {
        id: "product",
        accessorKey: "name",
        header: "Товар",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <>
              <MobileProductCard>
                <MobileProductHeader>
                  <ProductImage
                    style={{ backgroundImage: `url(${product.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{product.name}</ProductName>
                    <ProductModel>{product.model}</ProductModel>
                    <ProductColor>{product.color}</ProductColor>
                  </ProductDetails>
                </MobileProductHeader>
                <SizesContainer>
                  {groupSizesByValue(product.sizes)
                    .slice(0, 8)
                    .map((groupedSize, index) => (
                      <SizeChip key={index}>
                        {groupedSize.size}
                        {groupedSize.count > 1 && (
                          <span style={{ color: "#24BA7D" }}>
                            {" "}
                            x{groupedSize.count}
                          </span>
                        )}
                      </SizeChip>
                    ))}
                  {groupSizesByValue(product.sizes).length > 8 && (
                    <SizeCount style={{ marginLeft: "4px" }}>
                      +{groupSizesByValue(product.sizes).length - 8}
                    </SizeCount>
                  )}
                </SizesContainer>
                <MobileProductInfo>
                  <MobileProductPrice>
                    {formatPrice(
                      product.priceRange.min === product.priceRange.max
                        ? `${product.priceRange.min} Р`
                        : `${product.priceRange.min} - ${product.priceRange.max} Р`
                    )}
                  </MobileProductPrice>
                  <MobileProductPrice style={{ color: product.avgProfit >= 0 ? "#24BA7D" : "#FF5252" }}>
                    {formatPrice(`${product.avgProfit} Р`)}
                    <span style={{ fontSize: "11px", marginLeft: "4px", opacity: 0.8 }}>
                      ({product.avgProfitPercent > 0 ? '+' : ''}{product.avgProfitPercent}%)
                    </span>
                  </MobileProductPrice>
                  <MobileArticleWrapper>
                    <MobileProductArticle>{product.article}</MobileProductArticle>
                    <CopyArticleButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(product.article);
                      }}
                      title="Копировать артикул"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M9.66667 2.5H12.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.83333 4.16667C5.83333 3.25 6.58333 2.5 7.5 2.5H8.81667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.5 6.66667V11.8083C17.5 12.7167 16.7583 13.4667 15.85 13.4667"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.5 6.66667H15C12.8333 6.66667 12.5 6.33333 12.5 4.16667V1.66667L17.5 6.66667Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </CopyArticleButton>
                  </MobileArticleWrapper>
                </MobileProductInfo>
              </MobileProductCard>
              <DesktopContent>
                <ProductInfo>
                  <ProductImage
                    style={{ backgroundImage: `url(${product.imageUrl})` }}
                  />
                  <ProductDetails>
                    <ProductName>{product.name}</ProductName>
                    <ProductModel>{product.model}</ProductModel>
                    <ProductColor>{product.color}</ProductColor>
                  </ProductDetails>
                </ProductInfo>
              </DesktopContent>
            </>
          );
        },
      },
      {
        id: "size",
        accessorKey: "sizes",
        header: "Размер",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <DesktopContent>
              <SizeLabel>
                <SizesContainer>
                  {groupSizesByValue(product.sizes)
                    .slice(0, 12)
                    .map((groupedSize, index) => (
                      <SizeChip key={index}>
                        {groupedSize.size}
                        {groupedSize.count > 1 && (
                          <span style={{ color: "#24BA7D" }}>
                            {" "}
                            x{groupedSize.count}
                          </span>
                        )}
                      </SizeChip>
                    ))}
                  {groupSizesByValue(product.sizes).length > 12 && (
                    <SizeCount style={{ marginLeft: "4px" }}>
                      +{groupSizesByValue(product.sizes).length - 12}
                    </SizeCount>
                  )}
                </SizesContainer>
              </SizeLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const aSizes = rowA.original.sizes.map((s) =>
            parseFloat(s.size.replace(/[^\d.]/g, ""))
          );
          const bSizes = rowB.original.sizes.map((s) =>
            parseFloat(s.size.replace(/[^\d.]/g, ""))
          );
          const aSize = aSizes.length > 0 ? Math.min(...aSizes) : 0;
          const bSize = bSizes.length > 0 ? Math.min(...bSizes) : 0;
          return aSize - bSize;
        },
      },
      {
        id: "price",
        accessorKey: "priceRange",
        header: "Цена",
        cell: ({ row }) => {
          const product = row.original;
          return (
            <DesktopContent>
              <PriceLabel>
                {formatPrice(
                  product.priceRange.min === product.priceRange.max
                    ? `${product.priceRange.min} Р`
                    : `${product.priceRange.min} - ${product.priceRange.max} Р`
                )}
              </PriceLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          const avgPriceA = rowA.original.priceRange.min;
          const avgPriceB = rowB.original.priceRange.min;
          return avgPriceA - avgPriceB;
        },
      },
      {
        id: "profit",
        accessorKey: "avgProfit",
        header: "Ср. Прибыль",
        cell: ({ row }) => {
          const product = row.original;
          const profitColor = product.avgProfit >= 0 ? "#24BA7D" : "#FF5252";
          return (
            <DesktopContent>
              <PriceLabel style={{ color: profitColor }}>
                {formatPrice(`${product.avgProfit} Р`)}
                <span style={{ fontSize: "12px", marginLeft: "4px", opacity: 0.8 }}>
                  ({product.avgProfitPercent > 0 ? '+' : ''}{product.avgProfitPercent}%)
                </span>
              </PriceLabel>
            </DesktopContent>
          );
        },
        sortingFn: (rowA, rowB) => {
          return rowA.original.avgProfit - rowB.original.avgProfit;
        },
      },
      {
        id: "article",
        accessorKey: "article",
        header: "Артикул",
        cell: ({ row }) => {
          const product = row.original;
          
          const handleCopyArticle = (e: React.MouseEvent) => {
            e.stopPropagation();
            navigator.clipboard.writeText(product.article);
            // Можно добавить toast уведомление
          };

          return (
            <DesktopContent>
              <ArticleWrapper>
                <ArticleLabel>{product.article}</ArticleLabel>
                <CopyArticleButton
                  onClick={handleCopyArticle}
                  title="Копировать артикул"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.66667 2.5H12.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.83333 4.16667C5.83333 3.25 6.58333 2.5 7.5 2.5H8.81667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.5 6.66667V11.8083C17.5 12.7167 16.7583 13.4667 15.85 13.4667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17.5 6.66667H15C12.8333 6.66667 12.5 6.33333 12.5 4.16667V1.66667L17.5 6.66667Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </CopyArticleButton>
              </ArticleWrapper>
            </DesktopContent>
          );
        },
      },
    ],
    [formatPrice]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const toggleRowExpansion = (articleId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [articleId]: !prev[articleId],
    }));
  };

  if (isLoading && data.length === 0) {
    return (
      <div style={{ color: "#FFFFFF", textAlign: "center", padding: "24px 0" }}>
        Загрузка...
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div style={{ color: "#FFFFFF", textAlign: "center", padding: "24px 0" }}>
        Инвентарь пуст. Добавьте товары, нажав кнопку "Добавить".
      </div>
    );
  }

  return (
    <TableWrapper>
      <CardsContainer>
        <FilterRow>
          {table.getHeaderGroups()[0].headers.map((header) => (
            <FilterItem
              key={header.id}
              $clickable={header.column.getCanSort()}
              onClick={header.column.getToggleSortingHandler()}
            >
              <FilterLabel>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </FilterLabel>
              {header.column.getCanSort() && (
                <SortIcon
                  direction={
                    header.column.getIsSorted() === "asc"
                      ? "asc"
                      : header.column.getIsSorted() === "desc"
                      ? "desc"
                      : "none"
                  }
                />
              )}
            </FilterItem>
          ))}
        </FilterRow>

        {table.getRowModel().rows.map((row) => {
          const product = row.original;
          const isExpanded = expanded[product.article];

          return (
            <CardWithExpandWrapper key={product.article}>
              <CardContainer
                $expanded={isExpanded}
                onClick={() => toggleRowExpansion(product.article)}
              >
                <CardRow>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </React.Fragment>
                  ))}
                </CardRow>

                <ExpandableContent $expanded={isExpanded}>
                  {product.sizes.map((size) => (
                    <SizeVariantContainer
                      key={size.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SizeVariantContent>
                        <SizeVariantInfo>
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              backgroundColor: '#0E1F1F',
                              border: '1px solid #2C4A4A',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#FFFFFF',
                              display: 'inline-block',
                              minWidth: '80px',
                              textAlign: 'center'
                            }}>
                              {size.size}
                            </div>
                          </div>
                          <SizeVariantGrid>
                            <InfoPill>
                              <InfoLabel>Ср. рын.</InfoLabel>
                              <InfoValue>
                                {formatMarketPrice(String(size.avgMarketPrice || "—"))}
                              </InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>GOAT</InfoLabel>
                              <InfoValue>
                                {formatMarketPrice(String(size.goatPrice || "—"))}
                              </InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>StockX</InfoLabel>
                              <InfoValue>
                                {formatMarketPrice(String(size.stockXPrice || "—"))}
                              </InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Poizon</InfoLabel>
                              <InfoValue>
                                {formatMarketPrice(String(size.poizonPrice || "—"))}
                              </InfoValue>
                            </InfoPill>
                            <InfoPill>
                              <InfoLabel>Подробнее</InfoLabel>
                              <ProductLink
                                to={`/sneaker/${size.id}`}
                                state={{ from: "inventory" }}
                                style={{ fontSize: '12px', padding: '2px 0' }}
                              >
                                Открыть →
                              </ProductLink>
                            </InfoPill>
                          </SizeVariantGrid>
                        </SizeVariantInfo>

                        <SizeVariantActions>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                          }}>
                            <SizeVariantPrice>
                              {formatPrice(size.price)}
                            </SizeVariantPrice>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                              <StatusButton
                                $color="#24BA7D"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSold(size.id);
                                }}
                                title="Отметить как проданный"
                              >
                                Продано
                              </StatusButton>
                              <StatusButton
                                $color={size.inStore ? "#FF6B35" : "#609f94"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  size.inStore
                                    ? onShipment(
                                        size.id,
                                        `${product.model} ${product.color} ${size.size}`
                                      )
                                    : onHide(size.id, size.inStore);
                                }}
                                title={size.inStore ? "Отгрузить" : "Показать в магазине"}
                              >
                                {size.inStore ? "Отгрузить" : "Показать"}
                              </StatusButton>
                            </div>
                            <IconButtonsColumn>
                              <IconButton
                                onClick={() => onCopyLink(size.id)}
                                title="Копировать ссылку"
                              >
                                <CopyIcon />
                              </IconButton>
                              {accessStockroom && (
                                <IconButton
                                  onClick={() => onGeneratePromoCode(size.id)}
                                  title="Генерировать qr-code"
                                >
                                  <PromoCodeIcon />
                                </IconButton>
                              )}
                              <IconButton
                                onClick={() => onEdit(size, product)}
                                disabled={loadingProductData}
                                style={{
                                  opacity: loadingProductData ? 0.5 : 1,
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => onDelete(size.id)}>
                                <TrashIcon />
                              </IconButton>
                            </IconButtonsColumn>
                          </div>
                        </SizeVariantActions>
                      </SizeVariantContent>
                    </SizeVariantContainer>
                  ))}
                </ExpandableContent>
              </CardContainer>
            </CardWithExpandWrapper>
          );
        })}
      </CardsContainer>

      {/* Pagination */}
      {onPageChange && totalItems > 0 && (
        <PaginationContainer>
          <PaginationInfo>
            {data.length === 0
              ? 'Нет элементов'
              : `Показано ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} из ${totalItems}`
            }
          </PaginationInfo>
          {renderPaginationButtons(currentPage, Math.ceil(totalItems / itemsPerPage), onPageChange, isLoading)}
          {onItemsPerPageChange && (
            <ItemsPerPageContainer>
              <ItemsPerPageLabel>Показывать по:</ItemsPerPageLabel>
              <ItemsPerPageSelect
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                disabled={isLoading}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                {totalItems > 0 && <option value={totalItems}>Все</option>}
              </ItemsPerPageSelect>
            </ItemsPerPageContainer>
          )}
        </PaginationContainer>
      )}
    </TableWrapper>
  );
};

// Функция рендеринга кнопок пагинации
const renderPaginationButtons = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void,
  isLoading?: boolean
) => {
  const buttons = [];
  const maxVisiblePages = 5;

  if (currentPage > 1) {
    buttons.push(
      <PageButton
        key="first"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1 || isLoading}
      >
        <ArrowLeftDoubleIcon />
      </PageButton>
    );
  }

  if (currentPage > 1) {
    buttons.push(
      <PageButton
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
      >
        <ArrowLeftIcon />
      </PageButton>
    );
  }

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <PageButton
        key={i}
        $active={i === currentPage}
        onClick={() => onPageChange(i)}
        disabled={isLoading}
      >
        {i}
      </PageButton>
    );
  }

  if (currentPage < totalPages) {
    buttons.push(
      <PageButton
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
      >
        <ArrowRightIcon />
      </PageButton>
    );
  }

  if (currentPage < totalPages) {
    buttons.push(
      <PageButton
        key="last"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages || isLoading}
      >
        <ArrowRightDoubleIcon />
      </PageButton>
    );
  }

  return buttons;
};

// Иконки пагинации
const ArrowLeftDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.2 12.4333L3.96667 9.2C3.43334 8.66667 3.43334 7.78 3.96667 7.24667L7.2 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.0333 12.4333L8.79999 9.2C8.26666 8.66667 8.26666 7.78 8.79999 7.24667L12.0333 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.96667 12.4333L12.2 9.2C12.7333 8.66667 12.7333 7.78 12.2 7.24667L8.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.96667 12.4333L7.20001 9.2C7.73334 8.66667 7.73334 7.78 7.20001 7.24667L3.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0334 4.01334L6.80002 7.24667C6.26669 7.78 6.26669 8.66667 6.80002 9.2L10.0334 12.4333" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.96667 12.4333L9.20001 9.2C9.73334 8.66667 9.73334 7.78 9.20001 7.24667L5.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Иконки
const CopyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.3333 10.7417V14.0917C13.3333 16.575 12.4083 17.5 9.925 17.5H5.90833C3.425 17.5 2.5 16.575 2.5 14.0917V10.075C2.5 7.59167 3.425 6.66667 5.90833 6.66667H9.25833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3333 10.7417H10.8333C9.25 10.7417 8.58333 10.075 8.58333 8.49167V6.00833L13.3333 10.7417Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.66667 2.5H12.5"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.83333 4.16667C5.83333 3.24167 6.575 2.5 7.5 2.5H9.16667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 6.66667V11.7C17.5 12.575 16.8 13.3333 15.8333 13.3333"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.5 6.66667H15C13.5417 6.66667 12.9167 6.04167 12.9167 4.58333V2.08333L17.5 6.66667Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PromoCodeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="#fff"
  >
    <g clipPath="url(#clip0_4418_7094)">
      <path
        d="M2 9.75C1.59 9.75 1.25 9.41 1.25 9V6.5C1.25 3.6 3.61 1.25 6.5 1.25H9C9.41 1.25 9.75 1.59 9.75 2C9.75 2.41 9.41 2.75 9 2.75H6.5C4.43 2.75 2.75 4.43 2.75 6.5V9C2.75 9.41 2.41 9.75 2 9.75Z"
        fill="white"
      />
      <path
        d="M22 9.75C21.59 9.75 21.25 9.41 21.25 9V6.5C21.25 4.43 19.57 2.75 17.5 2.75H15C14.59 2.75 14.25 2.41 14.25 2C14.25 1.59 14.59 1.25 15 1.25H17.5C20.39 1.25 22.75 3.6 22.75 6.5V9C22.75 9.41 22.41 9.75 22 9.75Z"
        fill="white"
      />
      <path
        d="M17.5 22.75H16C15.59 22.75 15.25 22.41 15.25 22C15.25 21.59 15.59 21.25 16 21.25H17.5C19.57 21.25 21.25 19.57 21.25 17.5V16C21.25 15.59 21.59 15.25 22 15.25C22.41 15.25 22.75 15.59 22.75 16V17.5C22.75 20.4 20.39 22.75 17.5 22.75Z"
        fill="white"
      />
      <path
        d="M9 22.75H6.5C3.61 22.75 1.25 20.4 1.25 17.5V15C1.25 14.59 1.59 14.25 2 14.25C2.41 14.25 2.75 14.59 2.75 15V17.5C2.75 19.57 4.43 21.25 6.5 21.25H9C9.41 21.25 9.75 21.59 9.75 22C9.75 22.41 9.41 22.75 9 22.75Z"
        fill="white"
      />
      <path
        d="M14 18.25H10C7.58 18.25 6.25 16.92 6.25 14.5V9.5C6.25 7.08 7.58 5.75 10 5.75H14C16.42 5.75 17.75 7.08 17.75 9.5V14.5C17.75 16.92 16.42 18.25 14 18.25ZM10 7.25C8.42 7.25 7.75 7.92 7.75 9.5V14.5C7.75 16.08 8.42 16.75 10 16.75H14C15.58 16.75 16.25 16.08 16.25 14.5V9.5C16.25 7.92 15.58 7.25 14 7.25H10Z"
        fill="white"
      />
      <path
        d="M19 12.75H5C4.59 12.75 4.25 12.41 4.25 12C4.25 11.59 4.59 11.25 5 11.25H19C19.41 11.25 19.75 11.59 19.75 12C19.75 12.41 19.41 12.75 19 12.75Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_4418_7094">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const EditIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.16667 1.66667H7.5C3.33333 1.66667 1.66667 3.33333 1.66667 7.5V12.5C1.66667 16.6667 3.33333 18.3333 7.5 18.3333H12.5C16.6667 18.3333 18.3333 16.6667 18.3333 12.5V10.8333"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3666 2.51667L6.7999 9.08334C6.5499 9.33334 6.2999 9.825 6.2499 10.1833L5.89157 12.6917C5.75823 13.6 6.3999 14.2333 7.30823 14.1083L9.81657 13.75C10.1666 13.7 10.6582 13.45 10.9166 13.2L17.4832 6.63334C18.6166 5.5 19.1499 4.18334 17.4832 2.51667C15.8166 0.850004 14.4999 1.38334 13.3666 2.51667Z"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.425 3.45833C12.9833 5.45 14.5333 7 16.5333 7.56667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.5 4.98333C14.725 4.70833 11.9333 4.56667 9.15 4.56667C7.5 4.56667 5.85 4.65 4.2 4.81667L2.5 4.98333"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.08333 4.14167L7.26666 3.05C7.39999 2.25833 7.5 1.66667 8.90833 1.66667H11.0917C12.5 1.66667 12.6083 2.29167 12.7333 3.05833L12.9167 4.14167"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.7084 7.61667L15.1667 16.0083C15.075 17.3167 15 18.3333 12.675 18.3333H7.32502C5.00002 18.3333 4.92502 17.3167 4.83336 16.0083L4.29169 7.61667"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.60834 13.75H11.3833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.91666 10.4167H12.0833"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default InventoryTable;
